import $ from 'jquery';
import get from 'lodash/get';

import Format from 'app/src/data/Format';
import Payment from 'app/src/data/models/Payment';
import PaymentServiceTerminal, {
  EVENT_TYPES,
  TerminalEventMonitoring,
} from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';

const _DESCRIPTION = 'Lightspeed Payments Terminal';

// This class is an abstract implementation, we can't instantiate this directly, we need to choose from one of these:
// - app/src/payments/terminals/LightspeedPaymentsTerminal

export default class LightspeedPaymentsTerminal extends PaymentServiceTerminal {
  constructor(terminalModel) {
    super(terminalModel);

    this.terminalModel = terminalModel;
    this.terminalEventMonitoring = null;
  }

  /**
   * Returns a string describing the terminal.
   *
   * @returns {string}    A description of the terminal
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Communicates with the terminal to start a transaction.
   * Custom event Terminal.TerminalEvents.PAYMENT_STARTED is used to notify the terminal. The user can initiate
   * a cancel action whether the api supports it or not.
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {Number} paymentOptions.customerId  Optional - The customer ID associated with the sale
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  When true, the terminal will request for manual card entry
   * @param {Payment} paymentOptions.recoveredPayment Optional - When specified, the system will attempt to
   *                                                  continue the specified payment. This can occurr if there
   *                                                  is a pending payment in the sale that was unresolved.
   * @param {String} paymentOptions.captureMethod Optional - The capture method ('automatic' or 'manual')
   */
  processPayment(paymentOptions) {
    let deferred = $.Deferred();

    // Initiate the monitoring here instead of inside the constructor
    // to avoid catching too many events from instantiation
    this.terminalEventMonitoring = new TerminalEventMonitoring(
      get(this.terminalModel, 'attributes'),
      'LightspeedPaymentsTerminal'
    );
    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STARTED);
    this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STAGING);

    this.stagePayment(paymentOptions)
      .done(({ payment, terminalPayment }) => {
        this.terminalEventMonitoring.paymentId = payment.get('paymentID');
        this.terminalEventMonitoring.paymentStatus = payment.get('status');
        this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.STAGED);

        if (payment.get('paymentID')) {
          const event = new CustomEvent(Terminal.TerminalEvents.PAYMENT_STARTED, {
            detail: { payment },
          });
          window.dispatchEvent(event);
        }
        this.pollApiForPaymentStatus(payment)
          .done(() => {
            const paymentStatus = payment.get('status');
            const paymentTransaction = payment.paymentTransaction;

            // Save the model for later
            terminalPayment.serviceModel = payment;
            terminalPayment.model.set(
              'amount',
              Format.parseDecimal(payment.get('totalAmount'), terminalPayment.amount)
            );

            switch (paymentStatus) {
              case Payment.Status.APPROVED:
                deferred.resolve(
                  new TerminalTransactionResponse(
                    Terminal.Response.APPROVED,
                    paymentStatus,
                    terminalPayment
                  )
                );
                break;

              default:
                const paymentResponseMessage = paymentTransaction
                  ? paymentTransaction.get('message') || ''
                  : '';

                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.TERMINAL_ERROR,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
            }
          })
          .fail(() => {
            const paymentStatus = payment.get('status');
            const paymentTransaction = payment.paymentTransaction;
            const paymentResponseMessage = paymentTransaction
              ? paymentTransaction.get('message') || ''
              : '';

            terminalPayment.serviceModel = payment;

            // map the payment service status to the terminal response
            switch (paymentStatus) {
              case Payment.Status.DECLINED:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.DECLINED,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.ERROR:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.TERMINAL_ERROR,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.CANCELLED:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.CANCELLED_FROM_TERMINAL,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.UNKNOWN:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.UNKNOWN,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
            }
          })
          .always(() => {
            this.terminalEventMonitoring.paymentStatus = payment.get('status');
            this.terminalEventMonitoring.submitEvent(EVENT_TYPES.PAYMENT.COMPLETED);
          });
      })
      .fail((transactionResponse) => {
        deferred.reject(transactionResponse);
      });

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to cancel the request.
   */
  cancelTransaction() {
    let deferred = $.Deferred();

    // cleanup
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
      delete this._pollingTimer;
    }

    // this terminal type does not support cancelling via the POS
    deferred.reject();

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to check the status of the terminal.  Resolves with the appropriate
   * status.
   */
  checkStatus() {
    let deferred = $.Deferred();

    // There is no status check for an Adyen or TriPOS terminal so just assume it's online
    // and resolve the promise immediately
    deferred.resolve(Terminal.Status.ONLINE);

    return deferred.promise();
  }
}
